import { config } from '../../../../config';
import type { Tag } from '../types';

export const isHierarchyEnabled = (account: string | null | undefined) =>
    !!account && config.hierarchyAccounts.includes(account);

// Exact, case-sensitive paths preserve existing names. Never normalize stored names.
export const isDescendant = (name: string, parent: string) => name.startsWith(`${parent}/`);
export const isRegularPath = (name: string) =>
    name.split('/').every(segment => !!segment.trim() && segment === segment.trim());
export const canDeleteTag = (tag: Tag, tags: Tag[], complete: boolean) =>
    complete && !tags.some(other => other.id !== tag.id && isDescendant(other.name, tag.name));

export interface HierarchyNode {
    key: string;
    path: string;
    label: string;
    tags: Tag[];
    children: HierarchyNode[];
    irregular?: boolean;
}

export function buildHierarchy(tags: Tag[]): HierarchyNode[] {
    const roots: HierarchyNode[] = [];
    const nodes = new Map<string, HierarchyNode>();
    for (const tag of tags) {
        // Unusual legacy names remain visible and actionable by ID, as standalone entries.
        if (!isRegularPath(tag.name)) {
            roots.push({
                key: `tag:${tag.id}`,
                path: tag.name,
                label: tag.name || '(empty name)',
                tags: [tag],
                children: [],
                irregular: true,
            });
            continue;
        }
        let siblings = roots;
        let path = '';
        const segments = tag.name.split('/');
        for (const [index, segment] of segments.entries()) {
            path = index === 0 ? segment : `${path}/${segment}`;
            let node = nodes.get(path);
            if (!node) {
                node = { key: `path:${path}`, path, label: segment, tags: [], children: [] };
                nodes.set(path, node);
                siblings.push(node);
            }
            if (index === segments.length - 1) {
                node.tags.push(tag);
            }
            siblings = node.children;
        }
    }
    const sort = (nodes: HierarchyNode[]) => {
        nodes.sort((a, b) => a.path.localeCompare(b.path));
        for (const node of nodes) {
            sort(node.children);
        }
    };
    sort(roots);
    return roots;
}

export function searchHierarchy(nodes: HierarchyNode[], query: string): HierarchyNode[] {
    const terms = query.toLowerCase().split(/\s+/).filter(Boolean);
    if (!terms.length) {
        return nodes;
    }
    return nodes.flatMap(node => {
        const children = searchHierarchy(node.children, query);
        return terms.every(term => node.path.toLowerCase().includes(term)) || children.length
            ? [{ ...node, children }]
            : [];
    });
}

export function newHierarchyName(segment: string, parent: Tag | null, tags: Tag[]) {
    const trimmed = segment.trim();
    const name = parent ? `${parent.name}/${trimmed}` : trimmed;
    let error = '';
    if (!trimmed || segment.includes('/')) {
        error = 'Enter one nonempty segment without /.';
    } else if (parent && !isRegularPath(parent.name)) {
        error = 'Children cannot be created beneath an irregular path.';
    } else if (name.length > 100) {
        error = 'The full path must be at most 100 characters.';
    } else if (tags.some(tag => tag.name.toLowerCase() === name.toLowerCase())) {
        error = 'This full tag name is already in use.';
    }
    return { name, error };
}
